import type { Role } from "../api/types.ts"
import type { IconName } from "../ui/icons.tsx"
import type { CaseActionOwner } from "./case-actions.ts"

/**
 * "누구 일인가" 를 화면에 보이는 법 — 쿨트랙JP(일본 창고)와 화주.
 *
 * 두 회사가 같은 화면을 보면서 **자기 일만 골라내야 한다.** 그래서 상세 화면 어디서든
 * 같은 색·같은 아이콘·같은 이름표로 그린다(지금 할 일 · 요약 · 이력).
 *
 * **색만으로 구분하지 않는다.** 색약인 사람에게는 두 구획이 같아 보이고, 흑백으로 인쇄하면
 * 색이 아예 사라진다 — 그래서 색 옆에 늘 아이콘(상자 / 서류)과 이름표가 함께 간다
 * (`ui/Badge.tsx` 가 상태 배지에서 쓰는 것과 같은 원칙).
 *
 * 누가 무엇을 할 수 있는지의 원본은 서버의 `server/src/domain/permissions.ts` 이고,
 * 어떤 **동작**이 누구 것인지는 `case-actions.ts` 가 정한다. 여기서 더하는 것은
 * 어떤 **이력·정보 구획**이 누구 것인지, 그리고 그것을 어떻게 그리는지다.
 */
export type CaseOwner = CaseActionOwner

/** 일하는 차례 — 실물을 만지는 쿨트랙JP가 먼저 하고, 그 내용을 화주가 확인한다. */
export const CASE_OWNERS: readonly CaseOwner[] = ["warehouse", "shipper"]

/**
 * 담당별 아이콘. 색을 못 보는 화면에서도 갈리도록 **모양이 서로 다른 것**으로 고른다
 * — 쿨트랙JP는 상자를 다루고, 화주는 서류를 확인한다.
 */
export const OWNER_ICON: Record<CaseOwner, IconName> = {
  warehouse: "box",
  shipper: "doc",
}

/** 계정 역할 → 담당. 관리자는 두 쪽을 함께 보므로 어느 한쪽이 아니다. */
export function ownerOfRole(role: Role): CaseOwner | null {
  if (role === "warehouse") return "warehouse"
  if (role === "shipper") return "shipper"
  return null
}

/**
 * 이력 항목 → 그 일을 한 쪽.
 *
 * 서버가 남기는 이벤트 종류를 `permissions.ts` 의 표에 그대로 맞춘 것이다.
 * 모르는 이벤트는 `null` — 새 이벤트가 생겨도 화면이 틀린 색을 칠하지 않고 색 없이 그린다.
 */
const EVENT_OWNER: Record<string, CaseOwner> = {
  // ① 접수 — 주문을 아는 화주가 적는다
  intake_created: "shipper",
  intake_updated: "shipper",
  lines_replaced: "shipper",
  cancelled: "shipper",

  // ②③⑤ 수령·검수·인보이스 품목 — 실물을 들고 있는 쿨트랙JP가 적는다
  scanned_in: "warehouse",
  arrived_manually: "warehouse",
  orphan_linked: "warehouse",
  weight_set: "warehouse",
  freight_set: "warehouse",
  inspected: "warehouse",
  items_replaced: "warehouse",

  // ④⑥ 확인·처리방안·서류 확정 — 적은 쪽이 아닌 화주가 확인한다
  arrival_confirmed: "shipper",
  disposition_set: "shipper",
  review_requested: "shipper",
  doc_confirmed: "shipper",
  documented: "shipper",

  // ⑦⑧ 일본 구간 — 쿨트랙JP만 찍는다
  assigned_shipment: "warehouse",
  shipped: "warehouse",
  osaka_arrived: "warehouse",
  departed_jp: "warehouse",
  discarded: "warehouse",

  // ⑨ 한국 입고 — 물건이 한국 창고에 들어온 사실은 한국 쪽만 안다
  returned: "shipper",
}

export function ownerOfEvent(type: string): CaseOwner | null {
  // hasOwn 없이 `EVENT_OWNER[type]` 만 쓰면 "toString" 같은 이름에 프로토타입의 함수가 잡힌다.
  return Object.hasOwn(EVENT_OWNER, type) ? EVENT_OWNER[type] : null
}

/**
 * 요약 탭의 정보 구획 → 그 내용을 적는 쪽.
 *
 * 읽기만 하는 화면이지만 "이 칸의 값을 누가 적었나" 가 두 회사에겐 곧 책임 소재다.
 * 반품 상품 표는 접수수량(화주)과 검수수량(쿨트랙JP)이 한 표에 있어 한쪽으로 묶지 않는다 —
 * 칸 머리글마다 따로 표시한다.
 */
export const SUMMARY_OWNER = {
  intake: "shipper",
  arrival: "warehouse",
  handling: "shipper",
  shipment: "warehouse",
} as const satisfies Record<string, CaseOwner>
